import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { parseNoteBody } from '../../../scripts/release-notes/bodyMarkdown.mjs';
import { useAutoTranslation } from 'src/hooks/useTranslate';

/**
 * Renders published release notes: a dated, versioned timeline grouped into
 * New / Improved / Fixed / Removed / Security.
 *
 * Shared by the admin surface (Settings → Updates) and the all-user What's new
 * dialog, so the notes read identically wherever they are seen.
 */

// Mirrors TYPE_ORDER in scripts/release-notes/notes.js — readers scan for what is
// new before what was fixed. Any type absent from a release is skipped entirely.
const TYPE_ORDER = ['new', 'improved', 'fixed', 'removed', 'security'];

const TYPE_LABELS = {
  new: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
  removed: 'Removed',
  security: 'Security'
};

/**
 * One tinted chip per section, so the eye lands on the kind of change before
 * reading any of it.
 *
 * Deliberately applied to the five TYPES and not to `area`. Types are a closed,
 * semantic set — "something I can now do" vs "something that was broken" is the
 * axis people scan by. Areas are open-ended (the drafter coins them), so
 * colouring those means hashing names to hues: unpredictable, meaningless, and
 * repeated on every line, which would out-shout the section headers.
 *
 * Fixed hues rather than palette tokens: these encode meaning that should read
 * the same regardless of a tenant's configured accent colour. Every chip pairs
 * its colour with its own text, so colour is never the sole differentiator.
 */
const TYPE_STYLES = {
  new: { bg: '#eff6ff', fg: '#1d4ed8' }, // blue — an addition
  improved: { bg: '#f5f3ff', fg: '#6d28d9' }, // violet — an enhancement
  fixed: { bg: '#ecfdf5', fg: '#047857' }, // green — resolved
  removed: { bg: '#f1f5f9', fg: '#475569' }, // slate — a subtraction, de-emphasised
  security: { bg: '#fffbeb', fg: '#b45309' } // amber — notable, not alarming
};

const SECTION_CHIP_SX = (type) => ({
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TYPE_STYLES[type]?.fg || 'text.disabled',
  bgcolor: TYPE_STYLES[type]?.bg || 'action.hover',
  borderRadius: '4px',
  px: 0.875,
  py: 0.375,
  mt: 2,
  mb: 1.25
});

/**
 * Split the ISO date by hand rather than `new Date(iso)`: that parses as UTC
 * midnight, so anywhere west of Greenwich a release dated the 14th renders as
 * the 13th.
 */
export function formatReleaseDate(iso) {
  if (typeof iso !== 'string') return '';
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;

  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function groupNotesByType(notes = []) {
  return TYPE_ORDER.map((type) => ({
    type,
    label: TYPE_LABELS[type],
    notes: notes.filter((note) => note.type === type)
  })).filter((group) => group.notes.length > 0);
}

const VersionChip = ({ version }) => (
  <Box
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      fontFamily: 'monospace',
      color: 'text.secondary',
      bgcolor: 'action.hover',
      borderRadius: '4px',
      px: 0.75,
      py: 0.25,
      whiteSpace: 'nowrap'
    }}
  >
    {version}
  </Box>
);

/**
 * One inline run from the body parser. Text is emitted as a plain React text
 * node — never markup — so a body fetched from the release-notes feed (an
 * unauthenticated URL on some deployments) has no path into the DOM as HTML.
 */
const InlineRuns = ({ runs }) =>
  runs.map((run, index) => {
    if (run.kind === 'bold') {
      return (
        <Box key={index} component="strong" sx={{ fontWeight: 600 }}>
          {run.text}
        </Box>
      );
    }
    if (run.kind === 'italic') {
      return (
        <Box key={index} component="em">
          {run.text}
        </Box>
      );
    }
    if (run.kind === 'code') {
      // No fontFamily here on purpose: the sx class would beat the global
      // `code` element rule in index.css and swap the app's code stack for the
      // browser's generic monospace.
      return (
        <Box
          key={index}
          component="code"
          sx={{
            fontSize: '0.92em',
            bgcolor: 'action.hover',
            borderRadius: '3px',
            px: 0.4
          }}
        >
          {run.text}
        </Box>
      );
    }
    return run.text;
  });

/**
 * The optional body under a note's title. Bodies are authored as markdown and
 * frozen verbatim (hard source wraps included), so they cannot be dropped in
 * as text: rendering them with `white-space: pre-line` is what used to break
 * sentences mid-line and show literal ** and - markers (TGCLOUD-1746).
 * parseNoteBody soft-joins the wraps and yields the small block structure
 * rendered here.
 */
const NoteBody = ({ body }) => {
  const blocks = parseNoteBody(body);
  if (!blocks.length) return null;

  return (
    <Box sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.55 }}>
      {blocks.map((block, index) =>
        block.kind === 'list' ? (
          <Box
            key={index}
            component="ul"
            sx={{ my: 0, mt: index ? 0.75 : 0.25, pl: 2.5 }}
          >
            {block.items.map((item, itemIndex) => (
              <Box key={itemIndex} component="li" sx={{ mt: 0.5 }}>
                <InlineRuns runs={item} />
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            key={index}
            sx={{
              fontSize: 'inherit',
              color: 'inherit',
              lineHeight: 'inherit',
              mt: index ? 0.75 : 0.25
            }}
          >
            <InlineRuns runs={block.runs} />
          </Typography>
        )
      )}
    </Box>
  );
};

const NoteRow = ({ note }) => (
  <Box sx={{ display: 'flex', gap: 1.25, mb: 0.75 }}>
    {/* Carries the section's colour down the list, so a long run of fixes still
        says which section you are in without scrolling back to the header. */}
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        color: TYPE_STYLES[note.type]?.fg || 'text.disabled',
        lineHeight: 1.6,
        flexShrink: 0
      }}
    >
      •
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography component="span" sx={{ fontSize: 14, lineHeight: 1.6 }}>
        {note.area ? (
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: 14, mr: 0.5 }}
          >
            {note.area} —
          </Typography>
        ) : null}
        {note.title}
      </Typography>
      {note.body ? <NoteBody body={note.body} /> : null}
    </Box>
  </Box>
);

const ReleaseNotes = ({ release }) => {
  const t = useAutoTranslation();
  return groupNotesByType(release.notes).map((group) => (
    <Box key={group.type}>
      <Box sx={SECTION_CHIP_SX(group.type)}>{t(group.label)}</Box>
      {group.notes.map((note, index) => (
        <NoteRow key={`${group.type}-${index}`} note={note} />
      ))}
    </Box>
  ));
};

const ReleaseHeading = ({ release }) => (
  <>
    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
      {formatReleaseDate(release.date)}
    </Typography>
    <VersionChip version={release.version} />
  </>
);

const BLOCK_SX = (isLast) => ({
  pb: isLast ? 0 : 3,
  mb: isLast ? 0 : 3,
  borderBottom: isLast ? 'none' : '0.5px solid',
  borderColor: 'divider'
});

const ReleaseBlock = ({ release, isLast }) => (
  <Box sx={BLOCK_SX(isLast)}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2
      }}
    >
      <ReleaseHeading release={release} />
    </Box>
    <ReleaseNotes release={release} />
  </Box>
);

/**
 * An older release, collapsed to a single row until opened.
 *
 * This exists because the full history renders inside a settings card stacked
 * above other cards. Left expanded it pushes everything below it off the page,
 * and it only gets worse as releases accumulate. A fixed-height inner scroll
 * was the alternative and is worse: it traps the wheel, shrinks the reading
 * area, and still degrades with volume.
 */
const CollapsedRelease = ({ release, isLast }) => {
  const [open, setOpen] = useState(false);
  const panelId = `release-notes-${release.version}`;

  return (
    <Box sx={BLOCK_SX(isLast)}>
      <ButtonBase
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        aria-controls={panelId}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          textAlign: 'left',
          borderRadius: '6px',
          px: 1,
          mx: -1,
          py: 0.75,
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.disabled',
              flexShrink: 0,
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 150ms'
            }}
          />
          <ReleaseHeading release={release} />
        </Box>
        <Typography
          sx={{ fontSize: 12.5, color: 'text.disabled', whiteSpace: 'nowrap' }}
        >
          {`${release.notes.length} ${
            release.notes.length === 1 ? 'change' : 'changes'
          }`}
        </Typography>
      </ButtonBase>

      <Collapse in={open} unmountOnExit>
        <Box id={panelId} sx={{ pt: 0.5 }}>
          <ReleaseNotes release={release} />
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * @param {object[]} releases newest first
 * @param {number} [expandedCount] releases shown open; the rest collapse to a
 *   row. Defaults to every release — the What's new dialog is a continuous
 *   timeline with its own scroll and wants them all open.
 */
const ReleaseNotesList = ({
  releases = [],
  emptyMessage,
  expandedCount = Infinity
}) => {
  const t = useAutoTranslation();

  if (!releases.length) {
    return (
      <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
        {emptyMessage || t('No release notes to show yet.')}
      </Typography>
    );
  }

  return (
    <Box>
      {releases.map((release, index) => {
        const isLast = index === releases.length - 1;
        return index < expandedCount ? (
          <ReleaseBlock
            key={release.version}
            release={release}
            isLast={isLast}
          />
        ) : (
          <CollapsedRelease
            key={release.version}
            release={release}
            isLast={isLast}
          />
        );
      })}
    </Box>
  );
};

export default ReleaseNotesList;
